import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { toast } from 'sonner';

import i18n from '@/utils/i18n';
import { distinct } from '@/utils/arrayUtils';
import { isNullOrWhitespace } from '@/utils/stringHelper';
import { randomId } from '@/utils/random';
import uploadToS3 from '@/utils/imageUpload';
import { download } from '@/utils/download';
import { UserLanguage } from '@/utils/translation';
import { CurrencyIdByString } from '@/utils/price';
import useDebounce from '../../../hooks/useDebounce';

import { requestUploadUrl } from '../../../graphql/mutations';
import { allUserCountriesQuery } from '../../../graphql/queries';
import { ContainerTypeSegment } from '../../../graphql/generatedTypes';
import { useGetAllContainerTypeGroupsQuery } from '../../../graphql/queries/getAllContainerTypeGroups.generated';
import { useSyncContainerTypesToZohoMutation } from '../../../graphql/mutations/syncContainerTypesToZoho.generated';
import { useAddNewContainerTypeMutation } from '../../../graphql/mutations/addContainerType.generated';
import {
  GetAllContainerTypesAdminDocument,
  type GetAllContainerTypesAdminQuery,
  type GetAllContainerTypesAdminQueryVariables,
} from '../../../graphql/queries/getAllContainerTypesAdmin.generated';
import type { AdminContainerTypeFilter } from '../../../graphql/generatedTypes';
import {
  GetContainerTypeFacetsAdminDocument,
  type GetContainerTypeFacetsAdminQuery,
} from '../../../graphql/queries/getContainerTypeFacetsAdmin.generated';
import {
  GetAllContainerClassesAdminDocument,
  type GetAllContainerClassesAdminQuery,
} from '../../../graphql/queries/getAllContainerClassesAdmin.generated';
import {
  GetContainerPricingAdminDocument,
  type GetContainerPricingAdminQuery,
} from '../../../graphql/queries/getContainerPricingAdmin.generated';
import { useUpdateContainerTypeInformationAdminMutation } from '../../../graphql/mutations/updateContainerTypeInformationAdmin.generated';
import { useSaveContainerPricingAdminMutation } from '../../../graphql/mutations/saveContainerPricingAdmin.generated';
import { ViewerContext } from '../../../providers/ViewerContextProvider';
import { Role } from '../../../contants';
import type { ContainerType } from '../../ContainerComponents/ContainerPage';
import type {
  ContainerOverduePrice,
  StoreDefaultContainerPrice,
} from '../ContainerPriceTable';
import {
  deepClone,
  DRAFT_ID,
  expandPrice,
  fromGql,
  normalizeStoreRow,
  toInput,
  toInt,
  type ContainerTypeDraft,
  type OverduePricesByCurrency,
} from './containerTypeAdminMapping';
import {
  EMPTY_FILTER_STATE,
  toGqlContainerTypeFilter,
  sortForDisplay,
  type ContainerTypeFilterState,
} from './containerTypeFilters';
import { PAGE_SIZE } from './constants';
import { useContainerTypeDraft } from './useContainerTypeDraft';

export { DRAFT_ID } from './containerTypeAdminMapping';
export type { ContainerTypeDraft } from './containerTypeAdminMapping';

export type ContainerTypeFacets = {
  sizes: string[];
  brandings: string[];
  legalOwners: string[];
  maxOrderKey: number;
  maxId: number;
  totalCount: number;
};

const EMPTY_FACETS: ContainerTypeFacets = {
  sizes: [],
  brandings: [],
  legalOwners: [],
  maxOrderKey: 0,
  maxId: 0,
  totalCount: 0,
};

const MOBILE_PAGE_SIZE = 30;
const EXPORT_LIMIT = 10000;
// Unpaginated fetch of the whole container-type set (the equivalence picker needs
// every type, not just the current page). Matches the server's MAX_TAKE.
const ALL_TYPES_TAKE = 10000;
const SEARCH_DEBOUNCE_MS = 300;

/** One entry of the equivalence picker. See `equivalenceOptions`. */
export type EquivalenceOption = {
  value: number;
  label: string;
  /** Existing group membership, self included; empty when ungrouped. */
  groupTypeIds: number[];
};

/**
 * All data & actions of the redesigned Container Types screen: container type
 * info (formerly EditContainerInfo), per-container pricing (formerly
 * EditContainerPrices) and the create-draft flow (formerly
 * AddContainerTypeModal) — served by the colugo GraphQL admin API.
 *
 * The list is server-paginated and server-filtered; local edits live in an
 * overlay keyed by container type id so unsaved changes survive page flips
 * and filter changes until Save/Discard.
 */
export function useContainerTypesAdmin({ isMobile }: { isMobile: boolean }) {
  const client = useApolloClient();
  const viewer = useContext(ViewerContext);
  const isAdministrator = viewer.role === Role.administrator;

  // --- list state (server page + local edit overlay) ---
  const [filters, setFilters] =
    useState<ContainerTypeFilterState>(EMPTY_FILTER_STATE);
  const [pageIndex, setPageIndex] = useState(0);
  const [mobileLimit, setMobileLimit] = useState(MOBILE_PAGE_SIZE);
  const [pageItems, setPageItems] = useState<ContainerType[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [unfilteredCount, setUnfilteredCount] = useState(0);
  const [facets, setFacets] = useState<ContainerTypeFacets>(EMPTY_FACETS);
  const [containerClasses, setContainerClasses] = useState<
    { value: string; label: string }[]
  >([]);
  /**
   * Every container type as a target for the equivalence picker: id + name, plus
   * the full membership of the group it already belongs to (itself included, empty
   * when it has none) so picking one member can pull in its whole group.
   */
  const [equivalenceOptions, setEquivalenceOptions] = useState<
    EquivalenceOption[]
  >([]);

  /** Locally edited rows, keyed by id — the pending changes of the save bar. */
  const [edited, setEdited] = useState<Record<number, ContainerType>>({});
  /** Every row seen this session, so edits/bulk actions survive page flips. */
  const rowCache = useRef<Record<number, ContainerType>>({});

  const [storeDefaultContainerPrices, setStoreDefaultContainerPrices] =
    useState<StoreDefaultContainerPrice[]>([]);
  const [priceBaseline, setPriceBaseline] = useState<
    StoreDefaultContainerPrice[]
  >([]);
  const [consumerOverduePrices, setConsumerOverduePrices] =
    useState<OverduePricesByCurrency>({});
  const [overdueBaseline, setOverdueBaseline] =
    useState<OverduePricesByCurrency>({});

  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setUpdating] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [language, setLanguage] = useState<string>(UserLanguage.En);
  const [changedTypeIds, setChangedTypeIds] = useState<number[]>([]);
  const [priceTouchedIds, setPriceTouchedIds] = useState<number[]>([]);
  const [archiveChangedContainerTypeIds, setArchiveChangedContainerTypeIds] =
    useState<number[]>([]);

  // --- external data ---
  const [requestUploadUrlQuery] = useMutation(requestUploadUrl);
  const { data: countriesData = { __type: { enumValues: [] } } } = useQuery(
    allUserCountriesQuery,
  );
  const countries: { value: string; label: string }[] =
    countriesData.__type.enumValues.map((country: { name: string }) => ({
      label: country.name,
      value: country.name,
    }));

  const { data: containerTypeGroups, loading: loadingContainerTypeGroups } =
    useGetAllContainerTypeGroupsQuery({ fetchPolicy: 'no-cache' });

  const [updateContainerTypesMutation] =
    useUpdateContainerTypeInformationAdminMutation();
  const [savePricingMutation] = useSaveContainerPricingAdminMutation();
  const [syncContainerTypesToZoho] = useSyncContainerTypesToZohoMutation();
  const [addNewContainerTypeMutation] = useAddNewContainerTypeMutation();

  // --- derived lookups ---
  const groupOptions = useMemo(
    () =>
      (containerTypeGroups?.getAllContainerTypeGroups ?? []).map((g) => ({
        value: g.id.toString(),
        label: g.name,
      })),
    [containerTypeGroups],
  );
  const groupNameById = useMemo(() => {
    const map = new Map<number, string>();
    (containerTypeGroups?.getAllContainerTypeGroups ?? []).forEach((g) =>
      map.set(g.id, g.name),
    );
    return map;
  }, [containerTypeGroups]);

  /** Current page with pending edits applied, in display order. */
  const rows = useMemo(
    () => sortForDisplay(pageItems.map((ct) => edited[ct.id] ?? ct)),
    [pageItems, edited],
  );

  const duplicateSkus = useMemo(() => {
    const byId = new Map<number, ContainerType>();
    pageItems.forEach((ct) => byId.set(ct.id, ct));
    Object.values(edited).forEach((ct) => byId.set(ct.id, ct));
    const skus = [...byId.values()]
      .map((ct) => ct.sku)
      .filter((sku) => !isNullOrWhitespace(sku));
    return skus
      .filter((sku, index, self) => self.indexOf(sku) !== index)
      .filter(distinct);
  }, [pageItems, edited]);

  const dirtyIds = useMemo(
    () => [...changedTypeIds, ...priceTouchedIds].filter(distinct),
    [changedTypeIds, priceTouchedIds],
  );

  // --- server queries ---
  const debouncedSearch = useDebounce(filters.search, SEARCH_DEBOUNCE_MS);
  const gqlFilter = useMemo(
    () => toGqlContainerTypeFilter(filters, debouncedSearch),
    [filters, debouncedSearch],
  );
  const gqlFilterKey = JSON.stringify(gqlFilter);

  const fetchPage = useCallback(
    async (filter: AdminContainerTypeFilter, skip: number, take: number) => {
      const { data } = await client.query<
        GetAllContainerTypesAdminQuery,
        GetAllContainerTypesAdminQueryVariables
      >({
        query: GetAllContainerTypesAdminDocument,
        variables: { filter, skip, take },
        fetchPolicy: 'no-cache',
      });
      const page = data?.getAllContainerTypesAdmin;
      return {
        items: (page?.items ?? []).map(fromGql),
        totalCount: page?.total_count ?? 0,
        unfilteredCount: page?.unfiltered_count ?? 0,
      };
    },
    [client],
  );

  const refreshPage = useCallback(async () => {
    const take = isMobile ? mobileLimit : PAGE_SIZE;
    const skip = isMobile ? 0 : pageIndex * PAGE_SIZE;
    const page = await fetchPage(gqlFilter, skip, take);
    setPageItems(page.items);
    setTotalCount(page.totalCount);
    setUnfilteredCount(page.unfilteredCount);
    page.items.forEach((ct) => {
      rowCache.current[ct.id] = ct;
    });
    return page;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPage, gqlFilterKey, isMobile, mobileLimit, pageIndex]);

  const refreshFacets = useCallback(async () => {
    const { data } = await client.query<GetContainerTypeFacetsAdminQuery>({
      query: GetContainerTypeFacetsAdminDocument,
      fetchPolicy: 'no-cache',
    });
    const gqlFacets = data?.getContainerTypeFacetsAdmin;
    const next: ContainerTypeFacets = {
      sizes: gqlFacets?.sizes ?? [],
      brandings: gqlFacets?.brandings ?? [],
      legalOwners: gqlFacets?.legal_owners ?? [],
      maxOrderKey: gqlFacets?.max_order_key ?? 0,
      maxId: gqlFacets?.max_id ?? 0,
      totalCount: gqlFacets?.total_count ?? 0,
    };
    setFacets(next);
    return next;
  }, [client]);

  const applyPricingPayload = useCallback(
    (payload?: {
      store_default_container_prices_string: string;
      consumer_overdue_prices_string: string;
    }) => {
      if (payload == null) return;
      try {
        const defaults = (
          JSON.parse(
            payload.store_default_container_prices_string,
          ) as StoreDefaultContainerPrice[]
        )
          .map(expandPrice)
          .sort((a, b) => a.Id - b.Id);
        const overdue = JSON.parse(
          payload.consumer_overdue_prices_string,
        ) as OverduePricesByCurrency;
        setStoreDefaultContainerPrices(defaults);
        setPriceBaseline(deepClone(defaults));
        setConsumerOverduePrices(overdue);
        setOverdueBaseline(deepClone(overdue));
      } catch (e) {
        console.error('Failed to parse container pricing payload', e);
      }
    },
    [],
  );

  const refreshPricing = useCallback(async () => {
    const { data } = await client.query<GetContainerPricingAdminQuery>({
      query: GetContainerPricingAdminDocument,
      fetchPolicy: 'no-cache',
    });
    applyPricingPayload(data?.getContainerPricingAdmin);
  }, [client, applyPricingPayload]);

  // Every container type as an equivalence-picker option. Loaded unpaginated (and
  // unfiltered) on purpose: the type a lid should pair with is usually NOT on the
  // page the user is editing, so the picker cannot be limited to `rows`. Archived
  // types are included — a phased-out type can still share a lid with a live one.
  const refreshEquivalenceOptions = useCallback(async () => {
    const page = await fetchPage({}, 0, ALL_TYPES_TAKE);
    setEquivalenceOptions(
      page.items
        .map((ct) => {
          const partners = ct.containerEquivalentTypeIds ?? [];
          return {
            value: ct.id,
            label: ct.name || `#${ct.id}`,
            groupTypeIds:
              partners.length > 0
                ? [ct.id, ...partners].sort((a, b) => a - b)
                : [],
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label)),
    );
  }, [fetchPage]);

  // Initial load: classes + facets + pricing + equivalence options (+ the first
  // page via the page-effect below).
  useEffect(() => {
    Promise.all([
      client
        .query<GetAllContainerClassesAdminQuery>({
          query: GetAllContainerClassesAdminDocument,
          fetchPolicy: 'no-cache',
        })
        .then(({ data }) =>
          setContainerClasses(
            (data?.getAllContainerClassesAdmin ?? []).map((name) => ({
              value: name,
              label: name,
            })),
          ),
        ),
      refreshFacets(),
      refreshPricing(),
      refreshEquivalenceOptions(),
    ])
      .catch((e) => {
        console.error(e);
        toast.error(i18n.t('EditContainerInfo:toastLoadError'));
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter changes restart pagination — done synchronously in the setter (not
  // an effect) so the page effect below never fetches a stale page first.
  const setFiltersAndResetPage = useCallback(
    (next: ContainerTypeFilterState) => {
      setFilters(next);
      setPageIndex(0);
      setMobileLimit(MOBILE_PAGE_SIZE);
    },
    [],
  );

  // (Re-)fetch the page whenever filter / page / viewport mode change.
  useEffect(() => {
    refreshPage().catch((e) => {
      console.error(e);
      toast.error(i18n.t('EditContainerInfo:toastLoadError'));
    });
  }, [refreshPage]);

  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const hasMore = rows.length < totalCount;
  const loadMore = () => setMobileLimit((limit) => limit + MOBILE_PAGE_SIZE);

  // --- info edits (edit overlay; mirrors the original page's behaviour) ---
  const markChanged = useCallback((containerTypeId: number) => {
    setChangedTypeIds((oldIds) =>
      oldIds.includes(containerTypeId) ? oldIds : [...oldIds, containerTypeId],
    );
  }, []);
  const markArchiveChanged = useCallback((containerTypeId: number) => {
    setArchiveChangedContainerTypeIds((oldIds) =>
      oldIds.includes(containerTypeId) ? oldIds : [...oldIds, containerTypeId],
    );
  }, []);

  const draftApi = useContainerTypeDraft({ language, groupOptions });

  /**
   * Current version of a row: pending edit, else the freshest server version.
   *
   * `pageItems` is consulted before `rowCache` (and is a real dependency) so that
   * a refetch — e.g. right after saving — gives this callback a new identity and
   * re-renders the open editor. Reading the ref alone would leave consumers stuck
   * on the pre-save row until the next `edited` change.
   */
  const getRowById = useCallback(
    (containerTypeId: number): ContainerType | null =>
      edited[containerTypeId] ??
      pageItems.find((ct) => ct.id === containerTypeId) ??
      rowCache.current[containerTypeId] ??
      null,
    [edited, pageItems],
  );

  function updateContainerType(
    containerTypeId: number,
    attrName: string,
    attrValue: unknown,
  ) {
    if (!isAdministrator) return;
    if (containerTypeId === DRAFT_ID) {
      draftApi.updateDraftField(attrName, attrValue);
      return;
    }

    const base = getRowById(containerTypeId);
    if (base == null) {
      console.error(
        `Error updating container type. Could not find container type for id: ${containerTypeId}`,
      );
      return;
    }

    const next: any = {
      ...base,
      namesByLanguage: { ...base.namesByLanguage },
      countryCodes: [...(base.countryCodes ?? [])],
    };

    if (attrName === 'archived') {
      markArchiveChanged(containerTypeId);
    }

    if (attrName === 'namesByLanguage') {
      if (language === UserLanguage.En) {
        const differentLangs = Object.keys(next.namesByLanguage).filter(
          (l) =>
            next.namesByLanguage[l] !== next.namesByLanguage[UserLanguage.En],
        );
        const differentLangsWithValuesString = differentLangs
          .map((l) => `${l}: ${next.namesByLanguage[l]}`)
          .join(', ');
        if (
          differentLangs.length > 0 &&
          !confirm(
            i18n.t('EditContainerInfo:overwriteNamesConfirm', {
              values: differentLangsWithValuesString,
            }),
          )
        ) {
          return;
        }
        Object.keys(next.namesByLanguage).forEach(
          (l) => (next.namesByLanguage[l] = attrValue),
        );
      } else {
        next.namesByLanguage[language] = attrValue;
      }
    } else {
      next[attrName] = attrValue;
    }

    markChanged(containerTypeId);
    setEdited((prev) => ({ ...prev, [containerTypeId]: next }));
  }

  /**
   * Move a row one position up (direction 1) or down (-1) in display order —
   * pagination-proof, with no server round-trip.
   *
   * The persisted order is a dense sequence of unique positive integers
   * (top = highest), with 0 as the repeatable "unordered" bucket at the
   * bottom. Because saved keys are always integers and fractions live only in
   * the overlay (in memory), every row we *haven't* touched sits at a known
   * integer — so we can reconstruct the full ordered set from `facets`
   * (max positive key = M), the overlay's fractional movers, and the vacated
   * original slots, without loading off-page rows. We then assign the mover a
   * fraction between its target neighbours; the backend flattens everything
   * to clean integers on save (reindexOrderKeys).
   *
   * Only the mover's own overlay row changes here.
   */
  function reorder(containerTypeId: number, direction: 1 | -1) {
    if (!isAdministrator || containerTypeId === DRAFT_ID) return;
    const current = getRowById(containerTypeId);
    if (current == null) return;

    // Integer slots still occupied by untouched rows = [1..M] minus the
    // original slots of rows now sitting at a fraction in the overlay.
    const vacated = new Set<number>();
    Object.keys(edited).forEach((idStr) => {
      const original = rowCache.current[Number(idStr)];
      if (original != null && original.orderKey > 0) {
        vacated.add(original.orderKey);
      }
    });

    const positions = new Set<number>();
    for (let i = 1; i <= facets.maxOrderKey; i += 1) {
      if (!vacated.has(i)) positions.add(i);
    }
    Object.values(edited).forEach((row) => {
      if (row.orderKey > 0) positions.add(row.orderKey);
    });
    // The mover must not be its own neighbour.
    positions.delete(current.orderKey);
    const sorted = Array.from(positions).sort((a, b) => a - b);

    const k = current.orderKey;
    let newValue: number;

    if (direction === 1) {
      const above = sorted.filter((v) => v > k);
      if (k <= 0) {
        // Promote an unordered (0) row: enter the ordered list at the bottom.
        newValue = above.length ? above[0] / 2 : 1;
      } else if (above.length === 0) {
        newValue = k + 1; // already the top — nudge above everything
      } else {
        // Land just above the immediate upper neighbour.
        newValue = above.length >= 2 ? (above[0] + above[1]) / 2 : above[0] + 1;
      }
    } else {
      const below = sorted.filter((v) => v < k);
      if (below.length === 0) {
        newValue = 0; // demote the lowest ordered row to the unordered bucket
      } else if (below.length === 1) {
        newValue = below[0] / 2; // becomes the new lowest ordered row
      } else {
        // Land just below the immediate lower neighbour.
        newValue = (below[below.length - 1] + below[below.length - 2]) / 2;
      }
    }

    if (newValue === current.orderKey) return;
    markChanged(containerTypeId);
    setEdited((prev) => ({
      ...prev,
      [containerTypeId]: { ...current, orderKey: newValue },
    }));
  }

  function handleField(id: number, attr: string, value: unknown) {
    if (attr === 'name') {
      updateContainerType(id, 'namesByLanguage', value);
    } else if (attr === 'group') {
      updateContainerType(id, 'groupId', parseInt(value as string, 10));
    } else {
      updateContainerType(id, attr, value);
    }
  }

  function handleCountryToggle(id: number, code: string) {
    const ct = id === DRAFT_ID ? draftApi.draft : getRowById(id);
    if (!ct) return;
    const current = ct.countryCodes || [];
    const next = current.includes(code)
      ? current.filter((c) => c !== code)
      : [...current, code];
    updateContainerType(id, 'countryCodes', next);
  }

  // Equivalences are symmetric, but only the edited type's membership is sent —
  // the server pulls the partner types into the same group (and out of any group
  // they were in) and rejects contradictory memberships in one request. A type can
  // never be equivalent to itself.
  function handleEquivalentToggle(id: number, typeId: number) {
    if (typeId === id) return;
    const ct = id === DRAFT_ID ? draftApi.draft : getRowById(id);
    if (!ct) return;
    const current = ct.containerEquivalentTypeIds || [];
    if (current.includes(typeId)) {
      updateContainerType(
        id,
        'containerEquivalentTypeIds',
        current.filter((t) => t !== typeId),
      );
      return;
    }
    // Picking a type that already has equivalents joins its whole group: the
    // server would pull those partners in regardless (a type belongs to at most
    // one group), so showing anything less would misreport what gets saved.
    const joined =
      equivalenceOptions.find((option) => option.value === typeId)
        ?.groupTypeIds ?? [];
    const next = [...new Set([...current, typeId, ...joined])]
      .filter((t) => t !== id)
      .sort((a, b) => a - b);
    updateContainerType(id, 'containerEquivalentTypeIds', next);
  }

  function openFileSelector(containerTypeId: number) {
    if (!isAdministrator) return;
    const input = window.document.createElement('input');
    input.type = 'file';
    input.onchange = (e) => uploadImage(containerTypeId, e);
    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  }

  async function uploadImage(containerTypeId: number, e: any) {
    const { files } = e.target;
    const file = (files || [])[0];
    if (!file) return;
    const { type: filetype } = file;
    const uniqueFilename = `${randomId(6)}_containerType_image_${containerTypeId}`;
    const { data } = await requestUploadUrlQuery({
      variables: { filename: uniqueFilename, filetype },
    });
    if (data.requestUploadUrl) {
      const url = await uploadToS3(file, data.requestUploadUrl);
      if (url == null) {
        toast.error(i18n.t('EditContainerInfo:toastUploadError'));
      } else {
        updateContainerType(containerTypeId, 'imageUrl', url);
      }
    }
  }

  // --- pricing edits (mirrors EditContainerPrices / ContainerPriceTable) ---
  const markPriceTouched = useCallback((containerTypeId: number) => {
    if (containerTypeId === DRAFT_ID) return;
    setPriceTouchedIds((oldIds) =>
      oldIds.includes(containerTypeId) ? oldIds : [...oldIds, containerTypeId],
    );
  }, []);

  function getStoreRaw(
    containerTypeId: number,
    currency: string,
    key: string,
  ): string {
    const row: any = storeDefaultContainerPrices.find(
      (r) => r.Id === containerTypeId,
    );
    return String(row?.[currency]?.[key] ?? '0');
  }

  function setStoreRaw(
    containerTypeId: number,
    currency: string,
    key: string,
    raw: string,
  ) {
    if (!isAdministrator) return;
    setStoreDefaultContainerPrices((prev) => {
      const next = [...prev];
      let index = next.findIndex((r) => r.Id === containerTypeId);
      if (index === -1) {
        next.push(expandPrice({ Id: containerTypeId } as any));
        next.sort((a, b) => a.Id - b.Id);
        index = next.findIndex((r) => r.Id === containerTypeId);
      }
      const row: any = { ...next[index] };
      row[currency] = { ...row[currency], [key]: raw };
      next[index] = row;
      return next;
    });
    markPriceTouched(containerTypeId);
  }

  function getOverduePrice(
    currency: string,
    containerTypeId: number,
  ): ContainerOverduePrice {
    const currencyId = (CurrencyIdByString as any)[currency];
    const byContainerTypeId = consumerOverduePrices[currency];
    if (byContainerTypeId == null) {
      return {
        creditsOnReturnInCents: '0',
        priceInclVatInCents: '0',
        containerTypeId,
        currency: currencyId,
      };
    }
    if (byContainerTypeId[containerTypeId] == null) {
      return { ...byContainerTypeId[-1] };
    }
    return byContainerTypeId[containerTypeId];
  }

  function setOverdueRaw(
    currency: string,
    containerTypeId: number,
    key: string,
    raw: string,
  ) {
    if (!isAdministrator) return;
    const currencyId = (CurrencyIdByString as any)[currency];
    setConsumerOverduePrices((prev) => {
      const next = { ...prev };
      const byId = { ...(next[currency] ?? {}) };
      const current: any =
        byId[containerTypeId] != null
          ? { ...byId[containerTypeId] }
          : {
              ...(byId[-1] ?? {
                creditsOnReturnInCents: '0',
                priceInclVatInCents: '0',
                currency: currencyId,
              }),
            };
      current[key] = raw;
      current.containerTypeId = containerTypeId;
      byId[containerTypeId] = current;
      next[currency] = byId;
      return next;
    });
    markPriceTouched(containerTypeId);
  }

  // --- draft create (replaces AddContainerTypeModal) ---
  function applyDraftExtras(
    source: ContainerTypeDraft,
    created: ContainerType,
    maxOrderKey: number,
  ) {
    const extras: Partial<ContainerType> = { orderKey: maxOrderKey + 1 };
    if (source.sku) extras.sku = source.sku;
    if (source.sizeHelper) extras.sizeHelper = source.sizeHelper;
    if (source.class && source.class !== created.class) {
      extras.class = source.class;
    }
    if (source.countryCodes.length) {
      extras.countryCodes = [...source.countryCodes];
    }
    if (source.weight) extras.weight = source.weight;
    if (source.packingUnit) extras.packingUnit = source.packingUnit;
    if (source.packingInstructions) {
      extras.packingInstructions = source.packingInstructions;
    }
    if (source.branding) extras.branding = source.branding;
    if (source.legalOwner) extras.legalOwner = source.legalOwner;
    if (source.hexCode) extras.hexCode = source.hexCode;
    if (source.imageUrl) extras.imageUrl = source.imageUrl;
    if (source.archived) extras.archived = true;

    const nameOverrides: { [lang: string]: string } = {};
    Object.keys(source.namesByLanguage).forEach((lang) => {
      const value = source.namesByLanguage[lang];
      if (value && value !== source.name) nameOverrides[lang] = value;
    });

    setEdited((prev) => ({
      ...prev,
      [created.id]: {
        ...created,
        ...extras,
        namesByLanguage: { ...created.namesByLanguage, ...nameOverrides },
      },
    }));
    markChanged(created.id);
    if (extras.archived) markArchiveChanged(created.id);
  }

  /**
   * Creates the container type via the existing add mutation, then applies
   * everything the mutation cannot carry (SKU, countries, box colour, top
   * display order, ...) as pending edits — the new container comes back
   * "Edited" and the user reviews + saves to persist. Returns the new id.
   */
  async function createDraft(): Promise<number | null> {
    const draft = draftApi.draft;
    if (draft == null || !draftApi.canCreateDraft || draftSaving) return null;
    setDraftSaving(true);
    try {
      const result = await addNewContainerTypeMutation({
        variables: {
          container_type_name: draft.name.trim(),
          is_white_label: false,
          prices_by_currency_string: JSON.stringify(draft.pricesByCurrency),
          group_id: draft.groupId || undefined,
          segment: (draft.segment ?? '').toUpperCase() as ContainerTypeSegment,
        },
      });
      if (result.errors != null || !result.data?.addNewContainerType) {
        throw new Error(result.errors?.[0]?.message);
      }

      // addNewContainerType assigns max(Id) + 1; the fresh facets tell us
      // where that landed and what the new top display order is.
      const newFacets = await refreshFacets();
      const createdPage = await fetchPage({ ids: [newFacets.maxId] }, 0, 1);
      const created = createdPage.items[0];
      if (created != null) {
        rowCache.current[created.id] = created;
        applyDraftExtras(draft, created, newFacets.maxOrderKey);
      }
      await Promise.all([refreshPage(), refreshPricing()]);

      toast.success(i18n.t('EditContainerInfo:toastDraftCreated'));
      draftApi.closeDraft();
      return created?.id ?? null;
    } catch (e: any) {
      console.error(e);
      toast.error(
        `${i18n.t('AddContainerTypeModal:errorAddingContainerType')}${
          e?.message ? `: ${e.message}` : ''
        }`,
      );
      return null;
    } finally {
      setDraftSaving(false);
    }
  }

  // --- save / discard / bulk ---
  async function save() {
    if (!isAdministrator || duplicateSkus.length > 0 || dirtyIds.length === 0) {
      return;
    }
    setUpdating(true);
    try {
      if (changedTypeIds.length > 0) {
        const inputs = changedTypeIds
          .map((id) => edited[id])
          .filter((ct): ct is ContainerType => ct != null)
          .map(toInput);
        await updateContainerTypesMutation({
          variables: { container_types: inputs },
        });
      }

      if (priceTouchedIds.length > 0) {
        const overduePayload: any[] = [];
        for (const byId of Object.values(consumerOverduePrices)) {
          for (const price of Object.values(byId)) {
            if (price.containerTypeId !== -1) {
              overduePayload.push({
                ...price,
                priceInclVatInCents: toInt(price.priceInclVatInCents),
                creditsOnReturnInCents: toInt(price.creditsOnReturnInCents),
              });
            }
          }
        }
        const { data } = await savePricingMutation({
          variables: {
            store_default_container_prices_string: JSON.stringify(
              storeDefaultContainerPrices.map(normalizeStoreRow),
            ),
            consumer_overdue_prices_string: JSON.stringify(overduePayload),
          },
        });
        applyPricingPayload(data?.saveContainerPricingAdmin);
      }

      const zohoIds = [
        ...archiveChangedContainerTypeIds,
        ...priceTouchedIds,
      ].filter(distinct);
      if (zohoIds.length > 0) {
        await syncContainerTypesToZoho({ variables: { ids: zohoIds } });
      }

      // Cleared only after the refetch: dropping the overlay first would render
      // the pre-save server rows for the duration of the round-trip, which reads
      // as the just-saved edits being lost.
      // Equivalence options are refreshed too: a save can regroup types, and the
      // picker's group memberships would otherwise describe the pre-save world.
      await Promise.all([
        refreshPage(),
        refreshFacets(),
        refreshEquivalenceOptions(),
      ]);
      setEdited({});
      setChangedTypeIds([]);
      setPriceTouchedIds([]);
      setArchiveChangedContainerTypeIds([]);
      toast.success(
        zohoIds.length > 0
          ? i18n.t('EditContainerInfo:toastSaveSuccessZoho')
          : i18n.t('EditContainerInfo:toastSaveSuccess'),
      );
    } catch (e: any) {
      console.error(e);
      toast.error(
        `${i18n.t('EditContainerInfo:toastSaveError')}${
          e?.message ? `: ${e.message}` : ''
        }`,
      );
    }
    setUpdating(false);
  }

  function discard() {
    setEdited({});
    setStoreDefaultContainerPrices(deepClone(priceBaseline));
    setConsumerOverduePrices(deepClone(overdueBaseline));
    setChangedTypeIds([]);
    setPriceTouchedIds([]);
    setArchiveChangedContainerTypeIds([]);
  }

  function bulkArchive(rowsToArchive: ContainerType[]) {
    if (!isAdministrator) return;
    rowsToArchive.forEach((ct) => {
      rowCache.current[ct.id] = rowCache.current[ct.id] ?? ct;
      updateContainerType(ct.id, 'archived', true);
    });
  }

  // --- export ---
  function csvEscape(value: unknown): string {
    const str = value == null ? '' : String(value);
    if (/[",\n]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  }

  /**
   * Exports the given rows, or — without arguments — everything matching the
   * current filter (fetched in one large page from the server).
   */
  async function exportCsv(rowsArg?: ContainerType[]) {
    let rowsToExport = rowsArg;
    if (rowsToExport == null) {
      try {
        const page = await fetchPage(gqlFilter, 0, EXPORT_LIMIT);
        rowsToExport = page.items.map((ct) => edited[ct.id] ?? ct);
      } catch (e) {
        console.error(e);
        toast.error(i18n.t('EditContainerInfo:toastLoadError'));
        return;
      }
    }
    const headers = [
      'id',
      'name',
      'class',
      'group',
      'segment',
      'sku',
      'countries',
      'weight',
      'packingUnit',
      'branding',
      'legalOwner',
      'hexCode',
      'orderKey',
      'archived',
    ];
    const lines = [headers.join(',')];
    rowsToExport.forEach((ct) => {
      const name = ct.namesByLanguage?.[language] ?? ct.name ?? '';
      const values = [
        ct.id,
        name,
        ct.class,
        groupNameById.get(ct.groupId) ?? ct.groupId,
        ct.segment ?? '',
        ct.sku ?? '',
        (ct.countryCodes ?? []).join(' '),
        ct.weight ?? '',
        ct.packingUnit ?? '',
        ct.branding ?? '',
        ct.legalOwner ?? '',
        ct.hexCode ?? '',
        ct.orderKey,
        ct.archived,
      ];
      lines.push(values.map(csvEscape).join(','));
    });
    // Prefix a UTF-8 BOM so Excel opens the export with correct encoding.
    const blob = new Blob(['\ufeff' + lines.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    download(
      blob,
      `container_types_${new Date().toISOString().slice(0, 10)}.csv`,
    );
    toast.success(
      i18n.t('EditContainerInfo:toastExported', { count: rowsToExport.length }),
    );
  }

  const displayName = (ct: ContainerType) =>
    ct.namesByLanguage?.[language] ?? ct.name ?? '';

  return {
    // context
    isAdministrator,
    isLoading: isLoading || loadingContainerTypeGroups,
    isUpdating,
    language,
    setLanguage,
    displayName,
    // list
    rows,
    totalCount,
    unfilteredCount,
    filters,
    setFilters: setFiltersAndResetPage,
    pageIndex,
    setPageIndex,
    pageCount,
    hasMore,
    loadMore,
    getRowById,
    // data
    containerClasses,
    groupOptions,
    groupNameById,
    countries,
    facets,
    allBranding: facets.brandings,
    allLegalOwner: facets.legalOwners,
    equivalenceOptions,
    duplicateSkus,
    // dirty state
    changedTypeIds,
    priceTouchedIds,
    dirtyIds,
    // info edits
    updateContainerType,
    handleField,
    handleCountryToggle,
    handleEquivalentToggle,
    reorder,
    openFileSelector,
    // pricing
    getStoreRaw,
    setStoreRaw,
    getOverduePrice,
    setOverdueRaw,
    // draft
    draft: draftApi.draft,
    draftSaving,
    canCreateDraft: draftApi.canCreateDraft,
    openDraft: draftApi.openDraft,
    closeDraft: draftApi.closeDraft,
    updateDraftField: draftApi.updateDraftField,
    getDraftPrices: draftApi.getDraftPrices,
    setDraftPrice: draftApi.setDraftPrice,
    createDraft,
    // actions
    save,
    discard,
    bulkArchive,
    exportCsv,
  };
}

export type ContainerTypesAdmin = ReturnType<typeof useContainerTypesAdmin>;
